import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DoughnutChart from 'app/modules/analytics/components/shared/charts/DoughnutChart';
import { CHART_COLORS } from 'app/modules/analytics/components/shared/charts/chartConfig';
import { useReportPreviewRenderReadyBlock } from 'app/modules/analytics/modules/reports/context/ReportPreviewRenderReadyContext';
import NumberUtil from 'utils/number';
import type { OverviewResponse } from 'api/new-analytics/types';

const FORMAT_COLORS = [
  CHART_COLORS.defaultDark,
  CHART_COLORS.compareLight,
  CHART_COLORS.tertiary,
  CHART_COLORS.quaternary,
  CHART_COLORS.quinary,
];

interface Props {
  data: OverviewResponse;
}

const ReportPreviewOverviewActivity = ({ data }: Props) => {
  const { t } = useTranslation();
  const breakdown = data.post_format_breakdown;
  const { markReady } = useReportPreviewRenderReadyBlock(
    'overview-activity-doughnut',
    {
      enabled: (breakdown?.formats.length ?? 0) > 0,
      immediateReady: !breakdown || breakdown.formats.length === 0,
    },
  );

  const formats = useMemo(() => {
    if (!breakdown) {
      return [];
    }

    const total = breakdown.total_posts || 1;
    return breakdown.formats.map((format, index) => ({
      ...format,
      percent: Math.round((format.count / total) * 100),
      color: FORMAT_COLORS[index % FORMAT_COLORS.length],
    }));
  }, [breakdown]);

  const chartLabels = formats.map((format) => format.label);
  const chartData = formats.map((format) => format.count);
  const chartColors = formats.map((format) => format.color);

  return (
    <div
      className="tw-flex tw-flex-col tw-gap-4 tw-overflow-hidden tw-rounded-xl tw-border tw-border-grayscale-10 tw-bg-white tw-p-4 tw-shadow-sm"
      data-cy="analytics-overview-export_activity-card"
    >
      <span className="tw-text-base tw-font-semibold tw-text-grayscale-180">
        {t('activity', { defaultValue: 'Activity' })}
      </span>

      {!breakdown || formats.length === 0 ? (
        <div className="tw-flex tw-flex-1 tw-items-center tw-justify-center tw-py-8">
          <span className="tw-text-sm tw-text-grayscale-100">
            {t('noDataAvailable', { defaultValue: 'No data available' })}
          </span>
        </div>
      ) : (
        <div className="tw-flex tw-flex-1 tw-items-center tw-gap-4">
          <div className="tw-flex tw-min-w-[180px] tw-flex-col tw-gap-3">
            <span className="tw-text-xs tw-font-semibold tw-text-grayscale-180">
              {t('contentByFormat', { defaultValue: 'Content by format' })}
            </span>
            <div className="tw-flex tw-flex-col tw-gap-2">
              {formats.map((format) => (
                <div
                  key={format.format}
                  className="tw-flex tw-items-center tw-gap-2"
                >
                  <div
                    className="tw-h-3 tw-w-3 tw-shrink-0 tw-rounded"
                    style={{ backgroundColor: format.color }}
                  />
                  <span className="tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-grayscale-180">
                    {format.label} {format.percent}%
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="tw-flex tw-flex-1 tw-justify-center">
            <div className="tw-relative tw-h-[180px] tw-w-[180px]">
              <DoughnutChart
                labels={chartLabels}
                datasets={[
                  {
                    label: t('contentByFormat', {
                      defaultValue: 'Content by format',
                    }),
                    data: chartData,
                  },
                ]}
                colors={chartColors}
                height={180}
                disableAnimation
                onRendered={markReady}
              />
              <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-flex tw-flex-col tw-items-center tw-justify-center">
                <span className="tw-text-[40px] tw-font-semibold tw-leading-[48px] tw-text-grayscale-180">
                  {NumberUtil.compact(breakdown.total_posts)}
                </span>
                <span className="tw-text-xs tw-font-medium tw-text-grayscale-100">
                  {t('posts', { defaultValue: 'Posts' })}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportPreviewOverviewActivity;
